import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Archive, Download, Loader2, PanelRightClose } from "lucide-react";
import { cn } from "../../../lib/utils";
import { Button } from "../../ui/button";
import LoadingSpinner from "../../loading-spinner/LoadingSpinner";
import { uint8ArrayToBase64 } from "../../../lib/chatPdfApi";
import { base64ZipToUint8Array, downloadBase64Zip, titleCaseZipName } from "../../../lib/chatZipApi";
import { parseZipForPreview } from "../../../lib/zipPreview";
import ChatZipArchiveExplorer from "./ChatZipArchiveExplorer";

export default function ChatZipBinaryArtifactPane({
  attachment,
  onOpenPdfAttachment,
  className,
  overlayMode = false,
  onClose,
}) {
  const [selectedPath, setSelectedPath] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [parsed, setParsed] = useState({
    loading: true,
    entries: [],
    error: null,
    truncatedList: false,
    pdfBytesByPath: {},
  });

  const filename = titleCaseZipName(attachment?.filename);

  useEffect(() => {
    if (!attachment?.data) {
      setParsed({
        loading: false,
        entries: [],
        error: "Missing ZIP data.",
        truncatedList: false,
        pdfBytesByPath: {},
      });
      return undefined;
    }

    let cancelled = false;
    setParsed((p) => ({ ...p, loading: true }));

    const run = () => {
      try {
        const bytes = base64ZipToUint8Array(attachment.data);
        const result = parseZipForPreview(bytes);
        if (cancelled) return;
        if (result.error) {
          setParsed({
            loading: false,
            entries: [],
            error: result.error,
            truncatedList: false,
            pdfBytesByPath: {},
          });
          return;
        }
        setParsed({
          loading: false,
          entries: result.entries,
          error: null,
          truncatedList: Boolean(result.truncatedList),
          pdfBytesByPath: result.pdfBytesByPath || {},
        });
      } catch (err) {
        console.error("[ChatZipBinaryArtifactPane] parse error:", err);
        if (!cancelled) {
          setParsed({
            loading: false,
            entries: [],
            error: "Could not read this ZIP file.",
            truncatedList: false,
            pdfBytesByPath: {},
          });
        }
      }
    };

    window.setTimeout(run, 0);
    return () => {
      cancelled = true;
    };
  }, [attachment?.data]);

  useEffect(() => {
    const first = parsed.entries[0]?.path || null;
    setSelectedPath(first);
  }, [parsed.entries]);

  const handleDownload = useCallback(async () => {
    if (!attachment?.data || downloading) return;
    setDownloading(true);
    try {
      downloadBase64Zip(attachment.data, filename);
    } finally {
      setDownloading(false);
    }
  }, [attachment?.data, filename, downloading]);

  const listNotice = useMemo(() => {
    if (parsed.truncatedList) return `Showing first ${parsed.entries.length} files (list capped for performance).`;
    return null;
  }, [parsed.truncatedList, parsed.entries.length]);

  const resolvePdfAttachmentForEntry = useCallback(
    (entry) => {
      const bytes = parsed.pdfBytesByPath?.[entry.path];
      if (!bytes?.length) return null;
      const name = entry.displayName || entry.path.split("/").pop() || "document.pdf";
      const fn = name.toLowerCase().endsWith(".pdf") ? name : `${name}.pdf`;
      return {
        type: "pdf",
        filename: fn,
        data: uint8ArrayToBase64(bytes),
      };
    },
    [parsed.pdfBytesByPath]
  );

  if (!attachment?.data) {
    return (
      <div
        className={cn(
          "flex h-full min-h-0 w-full flex-col items-center justify-center gap-2 bg-muted/20 px-4 text-center text-sm text-muted-foreground",
          className
        )}
      >
        <Archive className="h-10 w-10 opacity-40" aria-hidden />
        <p>ZIP preview unavailable.</p>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex h-full min-h-0 w-full flex-col overflow-hidden rounded-none border-l border-border/80 bg-[hsl(220,14%,96%)] shadow-[inset_1px_0_0_hsl(220,13%,91%)] dark:border-border dark:bg-[hsl(240,6%,10%)] dark:shadow-[inset_1px_0_0_hsl(240,4%,18%)]",
        className
      )}
    >
      <header className="flex shrink-0 items-center gap-3 border-b border-border/70 bg-background/85 px-3 py-2.5 backdrop-blur-md dark:bg-[hsl(240,5%,12%)]/90">
        <div className="flex min-w-0 flex-1 flex-col gap-0.5 sm:flex-row sm:items-center sm:gap-2">
          <span className="hidden shrink-0 rounded-md bg-muted px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground sm:inline">
            ZIP
          </span>
          <div className="flex min-w-0 items-center gap-2">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#E85D3F]/12 text-[#B9422B] dark:bg-[#E85D3F]/18 dark:text-[#ff9a83]">
              <Archive className="h-4 w-4" strokeWidth={2} aria-hidden />
            </div>
            <div className="min-w-0">
              <span className="block truncate text-sm font-medium text-foreground" title={filename}>
                {filename}
              </span>
              <span className="block truncate text-[11px] text-muted-foreground">
                {parsed.loading ? "Reading archive…" : `${parsed.entries.length} files`}
              </span>
            </div>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-4">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="relative h-8 w-8"
            disabled={downloading}
            aria-busy={downloading}
            onClick={handleDownload}
            aria-label={downloading ? "Downloading ZIP" : "Download ZIP"}
          >
            <span className={cn("inline-flex", downloading && "invisible")}>
              <Download className="h-4 w-4" aria-hidden />
            </span>
            {downloading ? (
              <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <LoadingSpinner />
              </span>
            ) : null}
          </Button>
          {overlayMode && typeof onClose === "function" ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-orange-500 hover:bg-orange-500/10 hover:text-orange-600"
              onClick={onClose}
              aria-label="Close preview"
            >
              <PanelRightClose className="h-4 w-4" />
            </Button>
          ) : null}
        </div>
      </header>

      <div className="relative min-h-0 flex-1 overflow-hidden bg-[hsl(220,14%,94%)]/90 dark:bg-black/25">
        {parsed.loading ? (
          <div className="flex flex-col items-center gap-3 py-16 text-muted-foreground">
            <Loader2 className="h-8 w-8 animate-spin opacity-60" aria-hidden />
            <p className="text-sm">Parsing archive…</p>
          </div>
        ) : parsed.error ? (
          <div className="flex h-full flex-col items-center justify-center px-6 py-10 text-center">
            <div className="rounded-xl border border-border bg-background px-6 py-8 text-sm text-muted-foreground shadow-sm">
              <p className="font-medium text-foreground">{parsed.error}</p>
              <p className="mt-2 text-muted-foreground">Download the ZIP to open it locally.</p>
              <Button
                type="button"
                className="relative mt-4"
                size="sm"
                disabled={downloading}
                aria-busy={downloading}
                onClick={handleDownload}
              >
                <span className={cn("inline-flex items-center", downloading && "invisible")}>
                  <Download className="mr-2 h-4 w-4" aria-hidden />
                  Download
                </span>
                {downloading ? (
                  <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
                    <LoadingSpinner />
                  </span>
                ) : null}
              </Button>
            </div>
          </div>
        ) : (
          <ChatZipArchiveExplorer
            className="h-full"
            entries={parsed.entries}
            selectedPath={selectedPath}
            onSelectPath={setSelectedPath}
            listNotice={listNotice}
            resolvePdfAttachmentForEntry={resolvePdfAttachmentForEntry}
            onOpenPdfAttachment={onOpenPdfAttachment}
          />
        )}
      </div>
    </div>
  );
}
